import React, { KeyboardEvent } from 'react';
import type { ModalVariant } from '../types';
import './ConfirmModal.css';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (inputValue: string) => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ModalVariant;
  loading?: boolean;
  showInput?: boolean;
  inputLabel?: string;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  inputPlaceholder?: string;
  inputRequired?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Потвърждение',
  message,
  confirmText = 'Потвърди',
  cancelText = 'Отказ',
  variant = 'default',
  loading = false,
  showInput = false,
  inputLabel = '',
  inputValue = '',
  onInputChange = () => {},
  inputPlaceholder = '',
  inputRequired = false,
}) => {
  if (!isOpen) return null;

  const handleConfirm = (): void => {
    if (showInput && inputRequired && !inputValue.trim()) {
      return;
    }
    onConfirm(inputValue);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && !showInput) {
      handleConfirm();
    }
  };

  const getConfirmButtonClass = (): string => {
    switch (variant) {
      case 'danger':
        return 'btn btn-danger';
      case 'warning':
        return 'btn btn-warning';
      default:
        return 'btn btn-primary';
    }
  };

  return (
    <div className="confirm-modal-overlay" onClick={onClose} onKeyDown={handleKeyDown}>
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-modal-header">
          <h3>{title}</h3>
          <button className="confirm-modal-close" onClick={onClose} disabled={loading}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="confirm-modal-body">
          <p>{message}</p>

          {showInput && (
            <div className="confirm-modal-input-group">
              {inputLabel && <label>{inputLabel}</label>}
              <textarea
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                placeholder={inputPlaceholder}
                disabled={loading}
                rows={3}
                autoFocus
              />
            </div>
          )}
        </div>

        <div className="confirm-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            className={getConfirmButtonClass()}
            onClick={handleConfirm}
            disabled={loading || (showInput && inputRequired && !inputValue.trim())}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Зареждане...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
